import { useState } from 'react';
import { ChevronDownIcon, PrinterIcon, ReceiptIcon } from 'lucide-react';
import { toast } from 'sonner';
import { useAdmin } from '../AdminContext';
import { dayShort } from '../../core/format/dates';
import { printSaleReceipt } from './saleReceipt';
import { Badge } from '../ui/Badge';
import { EmptyState } from '../ui/EmptyState';
import { listBox } from '../ui/styles';
import { money } from '../../core/format/currency';
import { clock, methodLabel, refundMethodLabel } from './kassaLabels';
import { saleMethods } from '../../core/rules/kassa';
import { VoidSaleForm } from './VoidSaleForm';
import { SaleReturnForm } from './SaleReturnForm';
import { SaleMarginLines } from './SaleMarginLines';
import { printReturnCreditNote } from './returnCreditNote';
import { staffList, withWhom } from '../../core/rules/salonDays';
import type { Sale, SaleReturn } from '../../core/types/kassa';
type Props = {
  sales: Sale[];
  editable: boolean;
  onVoided: () => void;
  /** Retour boeken (alleen de eigenaar): op elke dag, ook na de afsluiting. */
  canReturn?: boolean;
};

/** De bonnen van de dag, nieuwste bovenaan. Open een bon voor de regels, om te corrigeren of voor een retour. */
export function SaleList({
  sales,
  editable,
  onVoided,
  canReturn = false
}: Props) {
  const [open, setOpen] = useState<string | null>(null);
  const {
    site
  } = useAdmin();
  const printReturn = async (r: SaleReturn) => {
    if (!(await printReturnCreditNote(r, site.salon))) toast.error('Creditnota openen lukte niet', {
      description: 'Probeer het opnieuw of gebruik een andere browser.'
    });
  };
  const print = async (s: Sale) => {
    // Alleen bij een afspraak, en alleen in een gedeelde salon (withWhom is anders leeg).
    const performer = s.appointmentId ? withWhom(staffList(site.staff), s.staffId) : '';
    if (!(await printSaleReceipt(s, site.salon, dayShort(s.day), performer))) toast.error('Bon openen lukte niet', {
      description: 'Probeer het opnieuw of gebruik een andere browser.'
    });
  };
  if (sales.length === 0) {
    return <EmptyState icon={ReceiptIcon} title="Nog geen bonnen" text="Een afgerekende bon verschijnt hier, met het bedrag per betaalmethode in het dagoverzicht." data-id="element-2488" data-mp="27kth0zui46t5" />;
  }
  return <ul className={listBox} aria-label="Bonnen" data-id="element-2489" data-mp="2gl85u96oc41e">
      {sales.map(s => {
      const isOpen = open === s.id;
      const voided = s.status === 'gecorrigeerd';
      const panelId = `bon-${s.id}`;
      return <li key={s.id} data-id="element-2490" data-mp="2qhl8vxo3zu8t">
            <button type="button" onClick={() => setOpen(isOpen ? null : s.id)} aria-expanded={isOpen} aria-controls={panelId} className="flex w-full items-center gap-x-4 px-4 py-3.5 text-left transition-colors duration-150 ease-soft hover:bg-shell sm:px-5" data-id="element-2491" data-mp="2qh1mo4rfejoz">
              <span className="w-12 shrink-0 text-[14px] tabular-nums text-muted" data-id="element-2492" data-mp="398uhm4q22ezc">{clock(s.at)}</span>
              <span className="min-w-0 flex-1" data-id="element-2493" data-mp="34asy5iopwdnc">
                <span className={`block truncate text-[14px] ${voided ? 'text-muted line-through' : 'text-ink'}`} data-id="element-2494" data-mp="34c433ltwfrg1">{s.customerName}</span>
                <span className="block truncate text-[12px] text-muted" data-id="element-2495" data-mp="3suxecf8pumz4">
                  {s.number} · {s.lines.length === 1 ? s.lines[0].name : `${s.lines.length} regels`}
                </span>
              </span>
              {voided ? <Badge tone="neutral" data-id="element-2496" data-mp="3ivyhrv186hsh">Gecorrigeerd</Badge> : <span className="hidden text-[12px] text-muted sm:inline" data-id="element-2497" data-mp="3iz2rt91dg7r2">{s.methodCents === 0 && (s.creditCents ?? 0) > 0 ? 'Tegoed' : s.voucherCents > 0 && s.methodCents === 0 ? 'Cadeaubon' : s.cashPartCents ? 'Pin + contant' : methodLabel[s.method]}</span>}
              <span className={`text-[14px] tabular-nums ${voided ? 'text-muted line-through' : 'text-ink'}`} data-id="element-2498" data-mp="2v8gjrhrdanh2">{money(s.totalCents)}</span>
              <ChevronDownIcon className={`h-4 w-4 shrink-0 text-muted transition-transform duration-150 ease-soft ${isOpen ? 'rotate-180' : ''}`} strokeWidth={1.5} aria-hidden="true" data-id="element-2499" data-mp="304fgbsmunbsk" />
            </button>
            {isOpen && <div id={panelId} className="space-y-4 border-t border-line bg-shell px-4 py-4 text-[13px] sm:px-5" data-id="element-2500" data-mp="2j4unm0c0kxq7">
                <ul className="space-y-1.5" data-id="element-2501" data-mp="2e5rkilflh1kv">
                  {s.lines.map((l, i) => <li key={i} className="flex justify-between gap-3" data-id="element-2502" data-mp="29et4lmyiq5i5" data-mpi={i}>
                      <span className="min-w-0" data-id="element-2503" data-mp="25dpmsuwwtzea">
                        <span className="text-ink" data-id="element-2504" data-mp="25cy0kap7de0o">
                          {l.kind === 'product' && l.qty > 1 ? `${l.qty} × ` : ''}
                          {l.name}
                        </span>
                        {l.kind === 'treatment' && l.fromKuur && <span className="text-muted" data-id="element-2505" data-mp="1vhwc7h2r4fz5"> · uit kuur</span>}
                        {l.discountReason && <span className="block text-[12px] text-muted" data-id="element-2506" data-mp="1mf515idsfm81">
                            Korting {money((l.listPriceCents - l.priceCents) * (l.kind === 'product' ? l.qty : 1))}: {l.discountReason}
                          </span>}
                      </span>
                      <span className="tabular-nums text-ink" data-id="element-2507" data-mp="12ldbf05lkwfa">{money(l.totalCents)}</span>
                    </li>)}
                </ul>
                <dl className="space-y-1 border-t border-line pt-3" data-id="element-2508" data-mp="17idjthpx6b9k">
                  {s.voucherCents > 0 && <div className="flex justify-between gap-3" data-id="element-2509" data-mp="1cd18phou57oo">
                      <dt className="text-muted" data-id="element-2510" data-mp="1gb8gvo58h0o9">Cadeaubon …{s.voucherLast4}</dt>
                      <dd className="tabular-nums text-ink" data-id="element-2511" data-mp="jn147jy8xwmc">{money(s.voucherCents)}</dd>
                    </div>}
                  {(s.creditCents ?? 0) > 0 && <div className="flex justify-between gap-3" data-id="element-2512" data-mp="sn7fcz6h2kic">
                      <dt className="text-muted" data-id="element-2513" data-mp="sp1zwcdu0alw">Tegoed</dt>
                      <dd className="tabular-nums text-ink" data-id="element-2514" data-mp="xn3eol485nrk">{money(s.creditCents ?? 0)}</dd>
                    </div>}
                  {(['pin', 'contant'] as const).map(m => {
              const cents = saleMethods(s)[m];
              return cents > 0 ? <div key={m} className="flex justify-between gap-3" data-id="element-2515" data-mp="8xwnlz6v2mvy">
                        <dt className="text-muted" data-id="element-2516" data-mp="4xvxhhaegn92">{methodLabel[m]}</dt>
                        <dd className="tabular-nums text-ink" data-id="element-2517" data-mp="4zp4wysu7bi4">{money(cents)}</dd>
                      </div> : null;
            })}
                  {s.cashReceivedCents !== undefined && <div className="flex justify-between gap-3" data-id="element-2518" data-mp="3mducthvuzoso">
                      <dt className="text-muted" data-id="element-2519" data-mp="3me40uzxcflvq">Ontvangen · wisselgeld</dt>
                      <dd className="tabular-nums text-ink" data-id="element-2520" data-mp="3hg2i6g3txoks">
                        {money(s.cashReceivedCents)} · {money(s.changeCents ?? 0)}
                      </dd>
                    </div>}
                </dl>
                <SaleMarginLines sale={s} data-id="element-2521" data-mp="3rauk1w4g2xld" />
                {s.correction && <p className="rounded-md bg-sand px-3 py-2 text-ink" data-id="element-2522" data-mp="33liagv57gnij">
                    Gecorrigeerd om {clock(s.correction.at)}: {s.correction.reason}
                    {s.correction.refunded && ` · teruggegeven: ${[s.correction.refunded.pinCents > 0 ? `${money(s.correction.refunded.pinCents)} pin` : '', s.correction.refunded.cashCents > 0 ? `${money(s.correction.refunded.cashCents)} contant` : ''].filter(Boolean).join(' en ')}`}
                  </p>}
                <button type="button" onClick={() => void print(s)} className="inline-flex items-center gap-1.5 text-[13px] text-ink underline decoration-line underline-offset-4 hover:decoration-ink" data-id="element-2523" data-mp="1wuw4l95p3rze">
                  <PrinterIcon className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden="true" data-id="element-2524" data-mp="1staicx79u1cj" />
                  Bon printen of bewaren
                </button>
                {(s.returns?.length ?? 0) > 0 && <div className="border-t border-line pt-3" data-id="element-2525" data-mp="hdvnbyymhjk2">
                    <p className="text-ink" data-id="element-2526" data-mp="1873b7gkivqp6">Retour</p>
                    <ul className="mt-1.5 space-y-1.5" data-id="element-2527" data-mp="144gdiz167bk2">
                      {(s.returns ?? []).map(r => <li key={r.id} className="flex items-baseline justify-between gap-3" data-id="element-2528" data-mp="z53tem1ooil6">
                          <span className="min-w-0" data-id="element-2529" data-mp="u6iqgev1rkt1">
                            <span className="block truncate text-ink" data-id="element-2530" data-mp="u7tvsj4550m9">{r.lines.map(l => `${l.qty > 1 ? `${l.qty} × ` : ''}${l.name}`).join(', ')}</span>
                            <span className="block text-[12px] text-muted" data-id="element-2531" data-mp="1muekfbt8eym4">
                              {dayShort(r.day)} {clock(r.at)} · {refundMethodLabel[r.method]} ·{' '}
                              <button type="button" onClick={() => void printReturn(r)} className="underline underline-offset-2 hover:text-ink" data-id="element-2532" data-mp="1d54rxuuh7dxh">
                                {r.number}
                              </button>
                              {' '}· {r.reason}
                            </span>
                          </span>
                          <span className="shrink-0 tabular-nums text-ink" data-id="element-2533" data-mp="2d603ydymv2vv">− {money(r.refundCents)}</span>
                        </li>)}
                    </ul>
                  </div>}
                <div className="flex flex-wrap items-start gap-2" data-id="element-2534" data-mp="2n1jpq7jhgl6w">
                  {/* Corrigeren (hele bon, op de dag zelf) alleen zolang er geen retour op staat. */}
                  {editable && !voided && !s.returns?.length && <VoidSaleForm sale={s} onVoided={onVoided} data-id="element-2535" data-mp="2w1q5k02nlx98" />}
                  {canReturn && !voided && <SaleReturnForm key={`${s.id}-${s.returns?.length ?? 0}`} sale={s} onDone={onVoided} data-id="element-2536" data-mp="3112r8hhsgya8" />}
                </div>
              </div>}
          </li>;
    })}
    </ul>;
}